import { Component, inject, signal, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';

import { NzTimelineModule } from 'ng-zorro-antd/timeline';

import { ProjectDataService } from '@rapider/project-data';
import { DataOperationEvent } from '@rapider/ai-chat';

import { TimelineNodeCardComponent } from '../timeline-node-card/timeline-node-card.component';
import { DATA_INSIGHT_CONFIG, getOperationTheme, buildFetchRecordsPath, DATA_INSIGHT_TIMELINE_CONFIG } from '../database.config';
import { selectEvents } from '../state/data-history.reducer';

/**
 * @class DataInsightTimelineComponent
 * @description A Smart Component that bridges the NgRx event stream with the ProjectDataService to render
 * a dynamic and rich timeline of AI-triggered data operations. Listens to events and lazily fetches records dynamically.
 */
@Component({
  selector: 'rapider-data-insight-timeline',
  standalone: true,
  imports: [
    CommonModule,
    NzTimelineModule,
    TimelineNodeCardComponent
  ],
  templateUrl: './data-insight-timeline.component.html'
})
export class DataInsightTimelineComponent {
  private readonly store = inject(Store);
  private readonly projectDataService = inject(ProjectDataService);

  public readonly config = DATA_INSIGHT_TIMELINE_CONFIG;
  public getOperationTheme = getOperationTheme;

  // Automatically streams history payloads from the decoupled NgRx Store
  public events = this.store.selectSignal(selectEvents);

  // Local caching dictionaries mapping specific operation Event IDs to their retrieved payloads and loading states
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetchedDataMap = signal<Record<string, any[]>>({});
  public loadingMap = signal<Record<string, boolean>>({});

  constructor() {
    // Intercepts state changes natively and initiates side-effect loading protocols for new timeline elements
    effect(() => {
      const evts = this.events();

      // Execute the imperative network loops outside of the reactive listener bound
      untracked(() => {
        evts.forEach(evt => {
          // DELETE events signify missing records inherently, skipping network fetching.
          if (evt.operationType !== 'DELETE' && evt.recordIds && evt.recordIds.length > 0) {

            // Only trigger HTTP requests for newly spawned, unseen events
            if (!this.fetchedDataMap()[evt.id] && !this.loadingMap()[evt.id]) {
              this.fetchEventData(evt);
            }
          }
        });
      });
    });
  }

  /**
   * Executes the dynamic, authorized GET request through the core workspace token system.
   * Maps results back into the isolated reactive caching dictionaries.
   * @param {DataOperationEvent} event - The specific operation tracking entity.
   */
  private fetchEventData(event: DataOperationEvent): void {
    this.loadingMap.update(map => ({ ...map, [event.id]: true }));

    const path = buildFetchRecordsPath(event.projectModelName, event.recordIds);

    this.projectDataService.executeProjectApiCall('GET', path, null).subscribe({
      next: (response) => {
        // Enforce strong array typings against unpredictable payload shapes
        const normalizedArray = Array.isArray(response) ? response : (response ? [response] : []);

        this.fetchedDataMap.update(map => ({ ...map, [event.id]: normalizedArray }));
        this.loadingMap.update(map => ({ ...map, [event.id]: false }));
      },
      error: (err) => {
        console.error(`[Data Insight Timeline] Failed to resolve entity data for event: ${event.id}`, err);
        this.fetchedDataMap.update(map => ({ ...map, [event.id]: [] }));
        this.loadingMap.update(map => ({ ...map, [event.id]: false }));
      }
    });
  }
}